<script lang="ts">
  // Pack-opening reveal, a row of face-down cards that flip up one-by-one, each
  // bursting with its rarity colour (SR/UR get a holographic foil + glow). Tap to
  // flip the next / reveal all. Honours the settings toggle + reduced-motion: when
  // animations are off it drops straight to the finished grid.
  import { fade, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import type { CardInfo } from "@ygoevo/yo2-data";
  import type { ShopPack, Rarity, GainedCard } from "@ygoevo/world-protocol";
  import { cardsByIds } from "../cards.ts";
  import { cardImage } from "../../data.ts";
  import { foilClass, foilGradeClass, foilColor, foilLabel, isShiny, rarityColor, rarityLabel } from "../rarity.ts";
  import { settings } from "../stores/settings.svelte.ts";
  import DataWindow from "./ui/DataWindow.svelte";
  import { createHotkey, onEscape } from "../hotkeys.ts";

  let {
    pack,
    gained,
    title = "Pack Opened",
    accent = "magenta",
    aggregate = false,
    onClose,
  }: {
    pack: ShopPack;
    gained: GainedCard[];
    title?: string;
    accent?: "cyan" | "magenta";
    /** Merge duplicate (card, finish) pulls into one slot with a ×N badge. Bulk "Open All"
     *  reveals are unbounded — one DOM node per pull would hang the tab on huge opens. */
    aggregate?: boolean;
    onClose: () => void;
  } = $props();

  const reduce =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animate = $derived(settings.packAnimations && !reduce);

  type Pull = { id: number; name: string; rarity: Rarity; foil: number; n: number };
  let pulls = $state<Pull[]>([]);
  let upTo = $state(0); // how many cards have flipped up
  let ready = $state(false);
  const RANK: Record<Rarity, number> = { N: 0, R: 1, SR: 2, UR: 3 };
  const best = $derived(pulls.reduce<Rarity>((b, p) => (RANK[p.rarity] > RANK[b] ? p.rarity : b), "N"));
  /** Best foil grade pulled (0 = none), drives the foil finale callout. */
  const bestFoil = $derived(pulls.reduce((b, p) => Math.max(b, p.foil), 0));
  const allUp = $derived(ready && upTo >= pulls.length);

  let timers: ReturnType<typeof setTimeout>[] = [];

  $effect(() => {
    let alive = true;
    (async () => {
      let infos: CardInfo[] = [];
      try {
        infos = await cardsByIds(gained.map((g) => g.cardId));
      } catch {
        infos = [];
      }
      if (!alive) return;
      const lut = new Map(infos.map((c) => [c.id, c]));
      const all = gained.map((g) => {
        const c = lut.get(g.cardId);
        return { id: g.cardId, name: c?.name ?? `Card #${g.cardId}`, rarity: (c?.rarity ?? "N") as Rarity, foil: g.foil ?? 0, n: 1 };
      });
      if (aggregate) {
        const merged = new Map<string, Pull>();
        for (const p of all) {
          const key = `${p.id}:${p.foil}`;
          const e = merged.get(key);
          if (e) e.n++;
          else merged.set(key, p);
        }
        pulls = [...merged.values()];
      } else {
        pulls = all;
      }
      ready = true;
      if (!animate) {
        upTo = pulls.length; // instant: all face-up
      } else {
        // Stagger the flips, but bound the whole sequence so a bulk "Open All" (dozens of
        // cards) reveals in a few seconds, not a minute. Tapping (revealAll/next) races ahead.
        const stride = Math.max(60, Math.min(520, Math.floor(5200 / pulls.length)));
        pulls.forEach((_, i) => {
          timers.push(setTimeout(() => { if (upTo < i + 1) upTo = i + 1; }, 420 + i * stride));
        });
      }
    })();
    return () => {
      alive = false;
      for (const t of timers) clearTimeout(t);
      timers = [];
    };
  });

  function advance() {
    if (!ready) return;
    if (allUp) return;
    upTo = upTo + 1; // flip the next one early on tap
  }
  function revealAll() {
    for (const t of timers) clearTimeout(t);
    timers = [];
    upTo = pulls.length;
  }

  // Escape reveals everything (then closes); Space/Enter flip the next card (or
  // close once all are up). `allow` lets these share Enter/Escape with the shop.
  onEscape(() => { if (allUp) onClose(); else revealAll(); });
  const tap = () => (allUp ? onClose() : advance());
  createHotkey("Space", tap, { conflictBehavior: "allow" });
  createHotkey("Enter", tap, { conflictBehavior: "allow" });
</script>

<div class="mobile-dialog-host fixed inset-0 z-[70] grid place-items-center" role="presentation">
  <!-- backdrop: tap to advance / reveal-all -->
  <button
    class="modal-scrim absolute inset-0 bg-void/55"
    transition:fade={{ duration: 200 }}
    onclick={() => (allUp ? undefined : revealAll())}
    aria-label="Reveal all"
  ></button>

  <!-- best-pull aura wash behind everything (a foil pull tints it its grade colour) -->
  {#if allUp && (isShiny(best) || bestFoil > 0)}
    <div
      class="pointer-events-none absolute inset-0"
      style="background: radial-gradient(60% 50% at 50% 45%, {bestFoil > 0 ? foilColor(bestFoil) : rarityColor(best)}22, transparent 70%)"
      transition:fade={{ duration: 400 }}
    ></div>
  {/if}

  <div
    class="mobile-full-dialog mobile-dialog-panel relative w-[min(900px,95vw)] px-4"
    role="dialog"
    aria-modal="true"
    aria-label="{title}: {pack.name}"
    transition:scale={{ start: 0.96, opacity: 0, duration: 240, easing: expoOut }}
  >
    <div class="mb-5 shrink-0 text-center phone:mb-2">
      <div class="font-display text-xs uppercase tracking-[0.24em]" style="color:{accent === 'magenta' ? 'var(--color-magenta)' : 'var(--color-cyan)'}">{title}</div>
      <div class="mt-0.5 font-display text-lg font-bold text-ink">{pack.name}</div>
    </div>

    <!-- card row (scrolls when a bulk open spills past one screen) -->
    <div class="mobile-dialog-scroll flex max-h-[62dvh] flex-wrap items-center justify-center gap-3 overflow-y-auto scroll-cyber px-1 sm:gap-4 phone:gap-1.5">
      {#each pulls as p, i (i)}
        {@const up = upTo > i}
        <div class="reveal-slot" class:pop={up}>
          <div class="flip" class:up>
            <!-- back -->
            <div class="face back chamfer-btn" style="--c:6px" aria-hidden={up}>
              <div class="back-emblem">◆</div>
            </div>
            <!-- front -->
            <div class="face front" class:shiny={isShiny(p.rarity) || p.foil > 0} style="--glow:{p.foil > 0 ? foilColor(p.foil) : rarityColor(p.rarity)}">
              <!-- wrapper (not the <img>) carries the foil sheen: ::after doesn't render on replaced elements -->
              <div class="card-art chamfer-btn {p.foil ? '' : foilClass(p.rarity)} {foilGradeClass(p.foil)}" style="--c:6px">
                <img src={cardImage(p.id)} alt={p.name} title={p.name} class="card-art-img" draggable="false" />
              </div>
              {#if up}
                {#if p.n > 1}
                  <span class="count-tag">×{p.n}</span>
                {/if}
                {#if p.foil > 0}
                  <span class="foil-tag" style="background:{foilColor(p.foil)}" title={foilLabel(p.foil)}>✦</span>
                {/if}
                <span
                  class="rarity-tag"
                  style="background:{rarityColor(p.rarity)}"
                  class:dark={p.rarity === 'UR'}
                >{p.rarity}</span>
              {/if}
            </div>
          </div>
        </div>
      {/each}
      {#if pulls.length === 0 && ready}
        <div class="py-10 text-sm text-ink-dim">Cards added to your collection.</div>
      {/if}
    </div>

    <!-- footer -->
    <div class="mt-7 shrink-0 text-center phone:mt-3">
      {#if !allUp}
        <span class="animate-pulse-soft font-display text-xs uppercase tracking-[0.2em] text-ink-dim">
          Tap to reveal
        </span>
      {:else}
        <div in:fade={{ duration: 200 }}>
          {#if bestFoil > 0}
            <div class="mb-3 font-display text-sm font-bold uppercase tracking-[0.16em]" style="color:{foilColor(bestFoil)};text-shadow:0 0 16px {foilColor(bestFoil)}">
              ✦ {foilLabel(bestFoil)} pull! ✦
            </div>
          {:else if isShiny(best)}
            <div class="mb-3 font-display text-sm font-bold uppercase tracking-[0.16em]" style="color:{rarityColor(best)};text-shadow:0 0 16px {rarityColor(best)}">
              {rarityLabel(best)} pull!
            </div>
          {/if}
          <button
            onclick={onClose}
            class="chamfer-btn bg-cyan/15 px-9 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-cyan transition-colors hover:glow-cyan"
            style="--c:8px"
          >
            Continue
          </button>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .reveal-slot {
    width: clamp(86px, 15vw, 150px);
    aspect-ratio: 59 / 86;
    perspective: 900px;
  }
  /* Phone: smaller cards so a 10-pull still reads as a spread, not a scroll shaft. */
  :global(.phone) .reveal-slot { width: clamp(52px, 8vw, 76px); }
  .reveal-slot.pop { animation: slot-pop 360ms cubic-bezier(0.16, 1, 0.3, 1) both; }
  @keyframes slot-pop {
    0% { transform: translateY(10px) scale(0.92); }
    60% { transform: translateY(0) scale(1.06); }
    100% { transform: none; }
  }

  .flip {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 520ms cubic-bezier(0.4, 0.8, 0.2, 1);
  }
  .flip.up { transform: rotateY(180deg); }

  .face {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
  .back {
    display: grid;
    place-items: center;
    background:
      radial-gradient(120% 90% at 50% 0%, color-mix(in oklab, var(--color-cyan) 22%, transparent), transparent 60%),
      linear-gradient(160deg, #0b1430, #060912);
    box-shadow: inset 0 0 0 1.5px color-mix(in oklab, var(--color-cyan) 35%, transparent);
  }
  .back-emblem {
    color: color-mix(in oklab, var(--color-cyan) 70%, transparent);
    font-size: 2rem;
    filter: drop-shadow(0 0 8px color-mix(in oklab, var(--color-cyan) 60%, transparent));
  }
  .front {
    transform: rotateY(180deg);
  }
  .front.shiny {
    filter: drop-shadow(0 0 14px var(--glow));
  }
  .card-art {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  .card-art-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .foil-tag {
    position: absolute;
    bottom: 4px;
    left: 4px;
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    font-size: 0.6rem;
    font-weight: 800;
    color: #06121a;
    text-shadow: 0 0 4px rgba(255, 255, 255, 0.7);
  }
  .rarity-tag {
    position: absolute;
    bottom: 4px;
    right: 4px;
    padding: 1px 5px;
    border-radius: 3px;
    font-family: Rajdhani, system-ui, sans-serif;
    font-size: 0.58rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #06121a;
  }
  .rarity-tag.dark { color: #2a1a00; }
  .count-tag {
    position: absolute;
    top: 4px;
    right: 4px;
    padding: 1px 5px;
    border-radius: 3px;
    background: color-mix(in oklab, var(--color-void, #060912) 82%, transparent);
    font-family: Rajdhani, system-ui, sans-serif;
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: var(--color-ink, #e8f2ff);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--color-cyan) 35%, transparent);
  }

  @media (prefers-reduced-motion: reduce) {
    .reveal-slot.pop { animation: none; }
    .flip { transition: none; }
  }
</style>
